import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { flushSync } from 'react-dom';
import { IStopArea } from '../../../../../models/stop-area.model';
import { AnalyticsService } from '../../../../../services/analytics-service';
import './style.scss';
import { StopAreaType } from '../../models/stop-area-type.enum';
import StopAreaButton from './components/StopAreaButton';
import StopAreaDescription from './components/StopAreaDescription';
import { StopAreaSlots } from '../../models/settings.model';
import { getDefinedStopAreas } from '../../helpers/settings.helper';

export default function StopAreaSelector({
  selectedStopAreaType,
  homeStopAreas,
  workStopAreas,
  onSelect,
}: {
  selectedStopAreaType: StopAreaType | undefined;
  homeStopAreas: StopAreaSlots;
  workStopAreas: StopAreaSlots;
  onSelect?: (type: StopAreaType) => void;
}) {
  // === STATE ===
  const [isExpanded, setIsExpanded] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);
  const selectedStopAreaButtonRef = useRef<HTMLButtonElement>(null);
  const notSelectedStopAreaButtonRef = useRef<HTMLButtonElement>(null);

  // === VARIABLES ===
  const homeStopAreaList = getDefinedStopAreas(homeStopAreas);
  const workStopAreaList = getDefinedStopAreas(workStopAreas);
  const stopAreaGroups: Array<{ stopAreas: IStopArea[]; type: StopAreaType }> = [
    { stopAreas: homeStopAreaList, type: StopAreaType.Home },
    { stopAreas: workStopAreaList, type: StopAreaType.Work },
  ].filter((group) => group.stopAreas.length > 0);
  const currentSelectedType = selectedStopAreaType ?? stopAreaGroups[0]?.type;
  const selectedGroup = stopAreaGroups.find((group) => group.type === currentSelectedType) ?? stopAreaGroups[0];
  const orderedStopAreaGroups = currentSelectedType
    ? [...stopAreaGroups].sort((groupA, groupB) => (groupA.type === currentSelectedType ? -1 : groupB.type === currentSelectedType ? 1 : 0))
    : stopAreaGroups;
  const visibleGroups = isExpanded ? orderedStopAreaGroups : selectedGroup ? [selectedGroup] : [];
  const notSelectedStopAreaType = stopAreaGroups.find((group) => group.type !== currentSelectedType)?.type;

  useEffect(() => {
    if (!isExpanded) return;

    function handleInteractionOutside(event: MouseEvent | FocusEvent): void {
      const target = event.target;
      if (!(target instanceof Node) || !selectorRef.current) return;
      if (selectorRef.current.contains(target)) return;

      setIsExpanded(false);
    }

    document.addEventListener('mousedown', handleInteractionOutside);
    document.addEventListener('focusin', handleInteractionOutside);

    return () => {
      document.removeEventListener('mousedown', handleInteractionOutside);
      document.removeEventListener('focusin', handleInteractionOutside);
    };
  }, [isExpanded]);

  // === EVENT HANDLERS ===

  function handleButtonClick(type: StopAreaType): void {
    if (type === currentSelectedType) {
      setIsExpanded(!isExpanded);
      return;
    }

    AnalyticsService.addEvent(AnalyticsService.MyStopAreas, 'Togglat hållplatsgrupper "Hemma" och "Arbete"');

    onSelect?.(type);
    setIsExpanded(false);
  }

  function handleButtonKeyDown(e: KeyboardEvent<HTMLButtonElement>): void {
    if (!currentSelectedType || !notSelectedStopAreaType) return;

    if (e.key === 'Escape' && isExpanded) {
      setIsExpanded(false);
      selectedStopAreaButtonRef.current?.focus();
      return;
    }

    if (e.key === 'Tab' && isExpanded) {
      if (e.currentTarget.id === `stop-area-${notSelectedStopAreaType}`) {
        e.preventDefault();
        const allFocusableElements = Array.from(
          document.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'),
        );
        const currentIndex = allFocusableElements.indexOf(e.currentTarget);
        if (currentIndex >= 0 && currentIndex < allFocusableElements.length - 1) allFocusableElements[currentIndex + 1].focus();
      }

      setIsExpanded(false);
      return;
    }

    if (e.key === 'ArrowDown') {
      if (!isExpanded) {
        flushSync(() => {
          setIsExpanded(true);
        });
      }

      if (e.currentTarget.id === `stop-area-${currentSelectedType}`) {
        e.preventDefault();
        notSelectedStopAreaButtonRef.current?.focus();
        return;
      }
    }

    if (e.key === 'ArrowUp' && isExpanded && e.currentTarget.id === `stop-area-${notSelectedStopAreaType}`) {
      e.preventDefault();
      selectedStopAreaButtonRef.current?.focus();
      return;
    }
  }

  // === RENDERING ===
  if (stopAreaGroups.length === 0) return null;

  if (stopAreaGroups.length === 1) {
    return (
      <div className="bg-white border rounded p-2 p-sm-3">
        <StopAreaDescription stopAreas={stopAreaGroups[0].stopAreas} type={stopAreaGroups[0].type} />
      </div>
    );
  }

  return (
    <div ref={selectorRef} id="stop-area-selector" className={`stop-area-selector ${isExpanded ? 'stop-area-selector--expanded' : ''} border bg-white d-flex flex-column`.trimEnd()}>
      {visibleGroups.map(({ stopAreas, type }) => (
        <StopAreaButton
          key={type}
          ref={type === currentSelectedType ? selectedStopAreaButtonRef : notSelectedStopAreaButtonRef}
          stopAreas={stopAreas}
          type={type}
          isSelected={type === currentSelectedType}
          isExpanded={type === currentSelectedType && isExpanded}
          onKeyDown={handleButtonKeyDown}
          onClick={() => handleButtonClick(type)}
        />
      ))}
    </div>
  );
}
